import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
interface Product {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string;
}
const FALLBACK_PRODUCTS: Product[] = [{
  title: 'THE PRESENCE',
  editionLabel: 'Core Web Architecture',
  priceNumber: 1500,
  priceText: '$1,500',
  thumbnailUrl: ''
}, {
  title: 'THE AUTHORITY',
  editionLabel: 'Web + Content Cycle',
  priceNumber: 3500,
  priceText: '$3,500',
  thumbnailUrl: ''
}, {
  title: 'THE EMPIRE',
  editionLabel: 'Full Digital Transformation',
  priceNumber: 8000,
  priceText: '$8,000',
  thumbnailUrl: ''
}];
const NAMES = ['James W.', 'Sarah M.', 'Michael R.', 'Emily T.', 'David K.', 'Jessica L.', 'Robert H.', 'Amanda C.', 'Christopher P.', 'Ashley B.', 'Daniel F.', 'Stephanie G.', 'Matthew N.', 'Lauren D.', 'Andrew S.', 'Rachel V.', 'Joshua E.', 'Megan A.', 'Ryan O.', 'Nicole J.', 'Brandon Z.', 'Samantha Q.', 'Kevin I.', 'Brittany U.', 'Justin Y.', 'Kayla X.', 'Tyler W.', 'Amber R.', 'Nathan M.', 'Heather L.'];
const CITIES = ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'San Antonio', 'San Diego', 'Dallas', 'Austin', 'San Francisco', 'Seattle', 'Denver', 'Boston', 'Nashville', 'Portland', 'Atlanta', 'Miami', 'Charlotte', 'Minneapolis', 'Tampa', 'Raleigh', 'Salt Lake City', 'San Jose', 'Scottsdale', 'Boise'];
function scanPageProducts(): Product[] {
  const products: Product[] = [];
  try {
    // Strategy 1: Look for JSON-LD structured data
    const jsonLdScripts = document.querySelectorAll('script[type="application/ld+json"]');
    jsonLdScripts.forEach(script => {
      try {
        const data = JSON.parse(script.textContent || '');
        if (data['@type'] === 'Product' && data.offers) {
          const offer = Array.isArray(data.offers) ? data.offers[0] : data.offers;
          products.push({
            title: data.name || 'Product',
            editionLabel: data.description || '',
            priceNumber: parseFloat(offer.price) || 0,
            priceText: `$${parseFloat(offer.price).toLocaleString()}`,
            thumbnailUrl: data.image || ''
          });
        }
      } catch {}
    });
    if (products.length > 0) return products;
    // Strategy 2: Scan for pricing cards/sections on the page
    const priceRegex = /\$[\d,]+(?:\.\d{2})?/g;
    const allElements = document.querySelectorAll('[class*="card"], [class*="tier"], [class*="pricing"], [class*="package"]');
    allElements.forEach(el => {
      const text = el.textContent || '';
      const priceMatches = text.match(priceRegex);
      if (!priceMatches) return;
      // Find title-like elements inside
      const headings = el.querySelectorAll('h2, h3, h4, [class*="title"], [class*="name"]');
      const img = el.querySelector('img');
      headings.forEach((heading, i) => {
        const price = priceMatches[i] || priceMatches[0];
        const priceNum = parseFloat(price.replace(/[$,]/g, ''));
        if (priceNum > 0) {
          // Find subtitle/description near the heading
          const subtitle = heading.nextElementSibling?.textContent?.trim() || '';
          products.push({
            title: heading.textContent?.trim() || 'Service',
            editionLabel: subtitle,
            priceNumber: priceNum,
            priceText: price,
            thumbnailUrl: img?.src || ''
          });
        }
      });
    });
    if (products.length > 0) return products;
    // Strategy 3: Broad scan for price patterns near headings
    const sections = document.querySelectorAll('section');
    sections.forEach(section => {
      const text = section.textContent || '';
      const prices = text.match(priceRegex);
      if (!prices) return;
      const headings = section.querySelectorAll('h3');
      headings.forEach((h, i) => {
        const price = prices[i];
        if (!price) return;
        const priceNum = parseFloat(price.replace(/[$,]/g, ''));
        if (priceNum >= 100) {
          const nextEl = h.parentElement?.querySelector('p, span, [class*="sub"]');
          products.push({
            title: h.textContent?.trim() || 'Service',
            editionLabel: nextEl?.textContent?.trim() || '',
            priceNumber: priceNum,
            priceText: price,
            thumbnailUrl: ''
          });
        }
      });
    });
  } catch {}
  return products;
}
function getRandomInterval(): number {
  return Math.floor(Math.random() * 7000) + 8000; // 8-15 seconds
}
export function RecentPurchaseToast() {
  const [visible, setVisible] = useState(false);
  const [notification, setNotification] = useState<{
    name: string;
    city: string;
    product: Product;
  } | null>(null);
  const productsRef = useRef<Product[]>([]);
  const nameHistoryRef = useRef<string[]>([]);
  const prefersReducedMotion = useRef(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    prefersReducedMotion.current = mq.matches;
    // Scan products after a short delay to ensure DOM is ready
    const scanTimer = setTimeout(() => {
      const scanned = scanPageProducts();
      productsRef.current = scanned.length > 0 ? scanned : FALLBACK_PRODUCTS;
    }, 1000);
    return () => clearTimeout(scanTimer);
  }, []);
  const pickName = useCallback((): string => {
    const available = NAMES.filter(n => !nameHistoryRef.current.includes(n));
    const pool = available.length > 0 ? available : NAMES;
    const name = pool[Math.floor(Math.random() * pool.length)];
    nameHistoryRef.current.push(name);
    if (nameHistoryRef.current.length > 10) {
      nameHistoryRef.current.shift();
    }
    return name;
  }, []);
  const pickProduct = useCallback((): Product => {
    const catalog = productsRef.current.length > 0 ? productsRef.current : FALLBACK_PRODUCTS;
    return catalog[Math.floor(Math.random() * catalog.length)];
  }, []);
  const showNotification = useCallback(() => {
    const name = pickName();
    const city = CITIES[Math.floor(Math.random() * CITIES.length)];
    const product = pickProduct();
    setNotification({
      name,
      city,
      product
    });
    setVisible(true);
    // Hide after 4 seconds
    setTimeout(() => {
      setVisible(false);
    }, 4000);
  }, [pickName, pickProduct]);
  useEffect(() => {
    // First notification after 3 seconds
    const firstTimer = setTimeout(() => {
      showNotification();
    }, 3000);
    return () => clearTimeout(firstTimer);
  }, [showNotification]);
  useEffect(() => {
    if (!visible && notification) {
      // Schedule next notification after current one hides
      const nextTimer = setTimeout(() => {
        showNotification();
      }, getRandomInterval());
      return () => clearTimeout(nextTimer);
    }
  }, [visible, notification, showNotification]);
  const reduced = prefersReducedMotion.current;
  return <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[90] pointer-events-none" data-id="element-234">
      <AnimatePresence data-id="element-235">
        {visible && notification && <motion.div initial={reduced ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 30
      }} animate={reduced ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} exit={reduced ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 30
      }} transition={{
        duration: reduced ? 0.1 : 0.4,
        ease: [0.22, 1, 0.36, 1]
      }} className="pointer-events-auto bg-[#1a1a1a] border border-white/10 rounded-lg shadow-2xl shadow-black/40 px-5 py-4 flex items-center gap-4 max-w-md w-max" data-id="element-236">
            {/* Product thumbnail or fallback icon */}
            {notification.product.thumbnailUrl ? <img src={notification.product.thumbnailUrl} alt={notification.product.title} className="w-12 h-12 rounded-md object-cover shrink-0" data-id="element-237" /> : <div className="w-12 h-12 rounded-md bg-blue-500/15 flex items-center justify-center shrink-0" data-id="element-238">
                <span className="text-blue-400 font-display font-bold text-xs" data-id="element-239">
                  PCS
                </span>
              </div>}

            <div className="min-w-0" data-id="element-240">
              <p className="text-white text-sm font-medium leading-snug" data-id="element-241">
                <span className="text-blue-400" data-id="element-242">{notification.name}</span> in{' '}
                {notification.city}
              </p>
              <p className="text-neutral-400 text-xs mt-0.5 truncate" data-id="element-243">
                just purchased{' '}
                <span className="text-neutral-300 font-medium" data-id="element-244">
                  {notification.product.title}
                </span>
                {notification.product.editionLabel && <span data-id="element-245"> · {notification.product.editionLabel}</span>}
                {' — '}
                <span className="text-green-400 font-semibold" data-id="element-246">
                  {notification.product.priceText}
                </span>
              </p>
              <p className="text-neutral-600 text-[10px] mt-1" data-id="element-247">
                a few moments ago
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}